import { useNavigate } from 'react-router-dom'
import React, { useState } from 'react'
import { FormModal, FormattedMessage, useIntl } from '@platform/cj-platform-navigation'
import styles from './EditProfile.module.css'
import { Text } from '@cjdev-internal/visual-stack-x/Text'
import { InputField } from '@cjdev-internal/visual-stack-x/InputField'
import { Button } from '@cjdev-internal/visual-stack-x/Button'
import useApi from '../../PlatformApi'
import { useAlert } from '@cjdev-internal/visual-stack-x/Alert'
import { useTabLayout, Tab, TabLayout } from '@cjdev-internal/visual-stack-x/TabLayout'
import { Clickable } from '@cjdev-internal/visual-stack-x/Clickable'
import { Grid } from '@cjdev-internal/visual-stack-x/Grid'
import { Row } from '@cjdev-internal/visual-stack-x/Row'
import { Stack } from '@cjdev-internal/visual-stack-x/Stack'
import { useToast } from '@cjdev-internal/visual-stack-x/Toast'

export const EditProfile = ({ user }) => {
  const navigate = useNavigate()
  const [isOpen, setIsOpen] = useState(true)
  const [firstName, setFirstName] = useState(user?.firstName)
  const [lastName, setLastName] = useState(user?.lastName)
  const [email, setEmail] = useState(user?.emailAddress)
  const [mount, showAlert, closeAlert] = useAlert()
  const api = useApi()
  const isEmployee = user?.isEmployee
  const [selectedId, selectTab] = useTabLayout('tab-0')
  const intl = useIntl()
  const [latestToastId, setLatestToastId] = useState(null)
  const [mountToast, show, dismiss] = useToast(true)

  const closeModal = () => {
    setIsOpen(false)
    navigate(-1)
  }

  const alertSuccessProps = {
    type: 'success',
    closeType: 'icon',
    children: 'Updated successfully.',
    onClose: closeAlert
  }

  const alertErrorProps = {
    type: 'warning',
    closeType: 'icon',
    children: 'Error.',
    onClose: closeAlert
  }

  const toastSuccessSendEmail = {
    message: intl.formatMessage({
      defaultMessage: 'Password reset email sent'
    }),
    contentStyle: { textAlign: 'center', minWidth: '300px' },
    type: 'success',
    duration: 5000
  }

  const toastErrorSendEmail = {
    message: intl.formatMessage({
      defaultMessage: 'Password reset email failed to send'
    }),
    contentStyle: { textAlign: 'center', minWidth: '300px' },
    type: 'warning',
    duration: 5000
  }

  const onLinkClicked = () => {
    api
      .changePassword(user.emailAddress)
      .then((response) => {
        if (response.ok) {
          showToastSendEmail(toastSuccessSendEmail)
        } else {
          showToastSendEmail(toastErrorSendEmail)
        }
      })
      .catch((e) => {
        showToastSendEmail(toastErrorSendEmail)
        console.error(e)
      })
  }

  const showToastSendEmail = (options) => {
    if (latestToastId) dismiss(latestToastId)
    const newToastId = show(options)
    setLatestToastId(newToastId)
  }

  const disableButton =
    (!firstName && !lastName) || (firstName === user.firstName && lastName === user.lastName)

  const save = async () => {
    try {
      const newUser = { firstName: firstName, lastName: lastName }
      await api.saveUser(user.userId, newUser)
      document.dispatchEvent(new Event('userUpdated'))
      showAlert(alertSuccessProps)
    } catch (e) {
      showAlert(alertErrorProps)
      console.error(e)
    }
  }

  return (
    <FormModal
      isOpen={isOpen}
      onRequestClose={closeModal}
      onSubmit={save}
      submitText={<FormattedMessage defaultMessage="Save" />}
      title={<FormattedMessage defaultMessage="Edit Profile" />}
    >
      <div className={styles.formModalContainer}>
        <div className={styles.formModalContainerRow}>
          <TabLayout
            floatingHeader={false}
            headerHeight={0}
            headerWidth={0}
            lazy={true}
            selectedId={selectedId}
            selectTab={selectTab}
          >
            <Tab id={'tab-0'} label={<FormattedMessage defaultMessage="Profile" />}>
              <Grid className={styles.profileContent} expand={true} gap={'large'}>
                <div className={styles.firstName}>
                  <InputField
                    label={<FormattedMessage defaultMessage="First Name" />}
                    value={firstName}
                    onChange={setFirstName}
                    disabled={isEmployee}
                    className={styles.formModalText}
                  />
                </div>

                <div className={styles.lastName}>
                  <InputField
                    label={<FormattedMessage defaultMessage="Last Name" />}
                    value={lastName}
                    onChange={setLastName}
                    disabled={isEmployee}
                    className={styles.formModalText}
                  />
                </div>
                <div className={styles.email}>
                  <InputField
                    label={<FormattedMessage defaultMessage="Email" />}
                    value={email}
                    onChange={setEmail}
                    disabled={disableButton}
                    className={styles.formModalText}
                  />
                </div>
                <div className={styles.button}>
                  <Button
                    type={disableButton ? 'secondary' : 'primary'}
                    disabled={disableButton}
                    uppercase={true}
                    onClick={save}
                  >
                    <FormattedMessage defaultMessage="Update Profile" />
                  </Button>
                </div>
              </Grid>
            </Tab>
            {user.currentCompany.features.has('NEW_PASSWORD_FLOW') && (
              <Tab id={'tab-1'} label={<FormattedMessage defaultMessage="Password" />}>
                <Row>
                  <Stack gap={'large'}>
                    <Text color="primary" size={16} weight={'medium'}>
                      <FormattedMessage defaultMessage="Change Password" />
                    </Text>
                    <Clickable onClick={onLinkClicked}>
                      <FormattedMessage defaultMessage="Send password reset email" />
                    </Clickable>
                  </Stack>
                </Row>
              </Tab>
            )}
          </TabLayout>
        </div>
      </div>
      {mount}
      {mountToast}
    </FormModal>
  )
}
